import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { getCookie } from '@utils/cookies';
import { useCallback } from 'react';

import { csrClient } from '../../../apollo/api/avantArteClient';
import {
  FF_FRAMING_CONTENT_RELEASE_PAGE,
  FF_FRAMING_GUARANTEE_COPY,
  FF_FRAMING_PREVIEWER_PRODUCT_PAGE,
  FF_FRAMING_PREVIEWER_RELEASE_PAGE,
  FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON,
} from '../../../constants/featureFlags';
import { AvantArteProductGateway } from '../../../gateways/AvantArteProductGateway';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useAuth } from '../../../providers/AuthProvider';
import { GeneratedTaxType } from '../../../types/generated';
import { AttributeInput } from '../../../types/Purchasing';
import { captureError } from '../../../utils/capture';

const productGateway = new AvantArteProductGateway(csrClient);

export type CheckoutAttributesArgs = {
  canBeFramed: boolean;
  privateLinkSlug?: string;
};

// Order attributes for reporting: experiment variations, who bought and where.
// Sent with Shopify checkouts and with draw entries, whose draft orders carry
// them onto the order.
export const useCheckoutAttributes = () => {
  const { isLoggedIn } = useAuth();
  const [countryCode] = useCountryCode();

  const productTileSubscriptionButton = useFeatureIsOn(
    FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON,
  );

  const framingGuaranteeCopy = useFeatureValue<string>(
    FF_FRAMING_GUARANTEE_COPY,
    'control',
  );

  const framingContentReleasePage = useFeatureIsOn(
    FF_FRAMING_CONTENT_RELEASE_PAGE,
  );
  const framingPreviewerProductPage = useFeatureIsOn(
    FF_FRAMING_PREVIEWER_PRODUCT_PAGE,
  );
  const framingPreviewerReleasePage = useFeatureIsOn(
    FF_FRAMING_PREVIEWER_RELEASE_PAGE,
  );

  return useCallback(
    async ({
      canBeFramed,
      privateLinkSlug,
    }: CheckoutAttributesArgs): Promise<AttributeInput[]> => {
      const attributes: AttributeInput[] = [
        {
          key: 'experimental_product_tile_subscription_button',
          value: String(productTileSubscriptionButton),
        },
        {
          key: 'experimental_framing_guarantee_copy',
          value: framingGuaranteeCopy,
        },
        {
          key: 'experimental_framing_content_release_page',
          value: String(framingContentReleasePage),
        },
        {
          key: 'experimental_framing_previewer_product_page',
          value: String(framingPreviewerProductPage),
        },
        {
          key: 'experimental_framing_previewer_release_page',
          value: String(framingPreviewerReleasePage),
        },
        { key: 'user_state', value: isLoggedIn ? 'LOGGED_IN' : 'LOGGED_OUT' },
      ];

      if (privateLinkSlug) {
        attributes.push({ key: 'private_link_id', value: privateLinkSlug });
      }

      attributes.push({ key: 'country_code', value: countryCode });
      // vat_rate is reporting only: a failed lookup must not block a checkout
      // or draw entry, so leave it out and carry on.
      try {
        const { tax } =
          await productGateway.getTaxesForCountryCode(countryCode);
        if (tax && tax.taxType === GeneratedTaxType.Vat) {
          attributes.push({ key: 'vat_rate', value: tax.vatRate.toString() });
        }
      } catch (error) {
        captureError(error);
      }

      const metaPixelFbc = getCookie('_fbc')?.split('.').at(-1);
      if (metaPixelFbc) {
        attributes.push({ key: 'custom_fbc', value: metaPixelFbc });
      }

      attributes.push({ key: 'can_be_framed', value: String(canBeFramed) });

      return attributes;
    },
    [
      countryCode,
      framingContentReleasePage,
      framingGuaranteeCopy,
      framingPreviewerProductPage,
      framingPreviewerReleasePage,
      isLoggedIn,
      productTileSubscriptionButton,
    ],
  );
};
